import { useCallback } from 'react';
import type { BoxCommunicationDetails, BoxQueryResult } from '@fednot/certinot-api';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { CommunicationsQueryKeyType } from './CommunicationsQueryKeys';
import CommunicationsQueryKeys from './CommunicationsQueryKeys';

export const communicationsQueryKeys = new CommunicationsQueryKeys();

export const useInvalidateCommunicationsCache = (type: CommunicationsQueryKeyType) => {
  const queryClient = useQueryClient();

  const invalidateCommunicationsCache = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: communicationsQueryKeys.getCommunications(type),
    });
  }, [queryClient, type]);

  return invalidateCommunicationsCache;
};

export const useInvalidateAllCommunicationsCache = () => {
  const queryClient = useQueryClient();

  const invalidateAllCommunicationsCache = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: communicationsQueryKeys.all(),
    });
  }, [queryClient]);

  return invalidateAllCommunicationsCache;
};

export const useInvalidateCommunicationCache = () => {
  const queryClient = useQueryClient();

  const invalidateCommunicationCache = useCallback(
    (communicationId: string) =>
      queryClient.invalidateQueries({
        queryKey: communicationsQueryKeys.getCommunication(communicationId),
      }),
    [queryClient],
  );

  return invalidateCommunicationCache;
};

type PagedCommunications = InfiniteData<BoxQueryResult>;

export const useUpdatePagedCommunicationsCache = () => {
  const queryClient = useQueryClient();

  const updatePagedCommunicationsCache = useCallback(
    (
      type: CommunicationsQueryKeyType,
      cb: (
        communications: BoxCommunicationDetails[],
        data: PagedCommunications,
      ) => BoxCommunicationDetails[],
    ) => {
      queryClient.setQueriesData<PagedCommunications>(
        { queryKey: communicationsQueryKeys.getCommunications(type) },
        (data) => {
          if (!data) return data;

          const allCommunications = data.pages.flatMap((page) => page.communications ?? []);
          const updatedCommunications = cb(allCommunications, data);
          const removedCount = allCommunications.length - updatedCommunications.length;
          const total = Math.max((data.pages[0]?.total ?? 0) - removedCount, 0);

          // Re-distribute items into the original page sizes; communication lists use different
          // page sizes per surface, so a fixed chunk size would corrupt their pagination.
          let cursor = 0;
          const pages = data.pages.map((page) => {
            const pageSize = page.communications?.length ?? 0;
            const communications = updatedCommunications.slice(cursor, cursor + pageSize);
            cursor += pageSize;
            return { ...page, communications, total };
          });

          return { ...data, pages };
        },
      );
    },
    [queryClient],
  );

  return updatePagedCommunicationsCache;
};
